import { useState } from "react";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

interface CNode {
  itemKind: "stock" | "semi" | "finished";
  itemCode: string;
  itemName: string;
  uom: string;
  lotId: string | null;
  lotCode: string | null;
  neededQty: number | null;
  consumedQty: number | null;
  isRaw: boolean;
  recorded: boolean;
  status: string | null;
  supplierLot: string | null;
  expiresAt: string | null;
  source: "batch" | "wo" | "receipt" | "recipe" | "unknown";
  sourceCode: string | null;
  children: CNode[];
}

interface Props {
  open: boolean;
  lotId: string | null;
  batchCode?: string;
  onClose: () => void;
}

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtDate(d: string | null): string {
  if (!d) return "";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

const KIND_META: Record<CNode["itemKind"], { label: string; color: string }> = {
  stock: { label: "HM", color: "var(--neutral-600)" },
  semi: { label: "YM", color: "var(--info)" },
  finished: { label: "MM", color: "var(--success-text)" },
};

function sourceChip(node: CNode): { text: string; color: string } {
  if (node.source === "batch") return { text: `Parti ${node.sourceCode ?? ""}`, color: "var(--primary)" };
  if (node.source === "wo") return { text: `İş Emri ${node.sourceCode ?? ""}`, color: "var(--primary)" };
  if (node.source === "receipt") return { text: node.supplierLot ? `Mal Kabul · ${node.supplierLot}` : "Mal Kabul", color: "var(--success-text)" };
  if (node.source === "recipe") return { text: "Reçeteye göre", color: "var(--neutral-400)" };
  return { text: "—", color: "var(--neutral-400)" };
}

function CNodeRow({ node, depth, isRoot }: { node: CNode; depth: number; isRoot: boolean }) {
  const [expanded, setExpanded] = useState(depth < 3);
  const hasChildren = node.children.length > 0;
  const km = KIND_META[node.itemKind];
  const src = sourceChip(node);
  const muted = !node.recorded; // teorik (reçeteye göre) satır

  return (
    <div>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 10px", paddingLeft: 10 + depth * 20,
          borderBottom: "1px solid var(--neutral-100)", fontSize: 13,
          background: isRoot ? "var(--primary-subtle)" : undefined,
          opacity: muted ? 0.82 : 1,
        }}
      >
        <button
          type="button"
          onClick={() => hasChildren && setExpanded((v) => !v)}
          aria-label={expanded ? "Daralt" : "Genişlet"}
          style={{
            width: 16, height: 16, border: 0, background: "transparent",
            color: "var(--neutral-500)", cursor: hasChildren ? "pointer" : "default",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            visibility: hasChildren ? "visible" : "hidden",
            transform: expanded ? "rotate(90deg)" : "none", transition: "transform 100ms",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
        </button>
        <span style={{ fontSize: 9.5, fontWeight: 600, color: km.color, background: "var(--neutral-100)", padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>{km.label}</span>
        {node.lotCode ? (
          <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--neutral-900)", flexShrink: 0 }}>{node.lotCode}</span>
        ) : (
          <span style={{ fontSize: 10, fontWeight: 600, color: "var(--neutral-400)", flexShrink: 0, fontStyle: "italic" }}>reçete</span>
        )}
        <span style={{ color: "var(--neutral-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
          {node.itemName}<span className="sec mono" style={{ fontSize: 10, marginLeft: 6 }}>{node.itemCode}</span>
        </span>
        <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)", flexShrink: 0, width: 78, textAlign: "right" }} title="Son kullanma tarihi">
          {node.expiresAt ? `SKT ${fmtDate(node.expiresAt)}` : ""}
        </span>
        {/* Reçeteye göre gereken */}
        <span className="mono" style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0, width: 96, textAlign: "right", color: "var(--neutral-500)", fontSize: 11.5 }} title="Reçeteye göre gereken">
          {isRoot ? "" : node.neededQty != null ? <>{fmtQty(node.neededQty)}<span style={{ color: "var(--neutral-400)", marginLeft: 2 }}>{node.uom}</span></> : "—"}
        </span>
        {/* Fiilen tüketilen */}
        <span className="mono" style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, flexShrink: 0, width: 110, textAlign: "right" }} title="Fiilen tüketilen">
          {isRoot ? (
            <span style={{ fontWeight: 500, fontSize: 11, color: "var(--neutral-500)" }}>üretilen {fmtQty(node.neededQty ?? 0)}<span style={{ color: "var(--neutral-400)", marginLeft: 2 }}>{node.uom}</span></span>
          ) : node.recorded && node.consumedQty != null ? (
            <span style={{ color: "var(--neutral-900)" }}>{fmtQty(node.consumedQty)}<span style={{ color: "var(--neutral-400)", fontSize: 10.5, marginLeft: 2, fontWeight: 500 }}>{node.uom}</span></span>
          ) : (
            <span style={{ color: "var(--neutral-400)", fontWeight: 500, fontSize: 10.5 }}>kayıt yok</span>
          )}
        </span>
        {/* Kaynak */}
        <span style={{ fontSize: 10, fontWeight: 600, color: src.color, flexShrink: 0, width: 160, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: muted ? "italic" : undefined }} title={src.text}>
          {src.text}
        </span>
      </div>
      {hasChildren && expanded ? node.children.map((c, i) => <CNodeRow key={c.lotId ?? `r-${c.itemCode}-${i}`} node={c} depth={depth + 1} isRoot={false} />) : null}
    </div>
  );
}

/** Üretilen bir lotun reçete kırılımı + FEFO fiili lot tüketimi — en alt hammaddeye dek. */
export function BatchConsumptionDrawer({ open, lotId, batchCode, onClose }: Props) {
  const traceQ = trpc.productionBatch.consumptionTrace.useQuery(
    { lotId: lotId! },
    { enabled: open && !!lotId, staleTime: 10_000 },
  );
  const root = traceQ.data as CNode | null | undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xxl"
      title="Tüketim Detayı (FEFO)"
      subtitle={batchCode ? `Parti: ${batchCode}` : "Üretilen lotun reçete + lot kırılımı"}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {!lotId ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--neutral-500)" }}>Bu partinin çıktı lotu yok.</div>
      ) : traceQ.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</div>
      ) : traceQ.isError ? (
        <div className="form-banner-err">{traceQ.error?.message}</div>
      ) : !root ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--neutral-500)" }}>Tüketim kaydı bulunamadı.</div>
      ) : (
        <>
          <p style={{ fontSize: 12.5, color: "var(--neutral-600)", margin: "0 0 12px", lineHeight: 1.5 }}>
            Üretilen lotun reçete ağacı en alttaki hammaddeye dek açılır (yarı mamuller → karışım/reçete → un, su, tuz…).
            Üretim anında fiilen stoktan düşülen lotlar <b>FEFO sırasına</b> göre gösterilir; kayıtlı tüketim yoksa satır
            <i> reçeteye göre</i> (teorik) listelenir.
          </p>
          <div className="d-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              <span style={{ width: 16 }} />
              <span style={{ flex: 1 }}>Lot · Ürün</span>
              <span style={{ width: 78, textAlign: "right" }}>SKT</span>
              <span style={{ width: 96, textAlign: "right" }}>Reçete</span>
              <span style={{ width: 110, textAlign: "right" }}>Tüketilen</span>
              <span style={{ width: 160, textAlign: "right" }}>Kaynak</span>
            </div>
            <CNodeRow node={root} depth={0} isRoot />
          </div>
        </>
      )}
    </Drawer>
  );
}
